<!-- ADD META TAGS FOR SEARCH BOOSTING -->
<template>
  <div>
    <DateChecker :boardId="boardId" />
    <NoDataToast
      v-if="isNoDataPopup"
      :category="noDataPopupCategory"
      :country-name="noDataPopupCountry"
      @close="hideNoDataPopup"
    />

    <loading-spinner v-if="loading" />
    <div v-else>
      <div :key="gameData" v-if="gameData && countryData">
        <div class="board">
          <div class="panel">
            <div class="row">
              <div class="filler" />
              <div
                v-for="(column, index) in gameData.columns"
                :key="'col-' + index"
                class="prompt column-prompt"
                @click="openCategory(column.id)"
              >
                {{ column.name }}
              </div>
            </div>
            <div v-for="(row, rowIndex) in gameData.rows" :key="'row-' + rowIndex" class="row">
              <div class="prompt row-prompt" @click="openCategory(row.id)">
                {{ row.name }}
              </div>
              <div
                v-for="(column, colIndex) in gameData.columns"
                :key="'col-' + colIndex"
                class="box"
                @click="!isGameOver && !board[rowIndex][colIndex] && showSearch(rowIndex, colIndex)"
                :class="{
                  highlighted:
                    searching && selectedRowIndex === rowIndex && selectedColIndex === colIndex,
                  'wrong animate-shake':
                    wrongGuessLocation.length === 2 &&
                    wrongGuessLocation[0] === rowIndex &&
                    wrongGuessLocation[1] === colIndex,
                  guessed: isGameOver || board[rowIndex][colIndex],
                }"
              >
                <div
                  class="rarity-element"
                  v-if="board[rowIndex][colIndex] !== ''"
                  :style="getRarityStyle(rowIndex, colIndex)"
                >
                  {{ getRarityText(rowIndex, colIndex) }}
                </div>
                <div v-if="board[rowIndex][colIndex] !== ''" class="flag-container">
                  <img
                    v-if="
                      guessedCountries.includes(board[rowIndex][colIndex]) &&
                      getImageUrl(board[rowIndex][colIndex]) !== ''
                    "
                    :src="getImageUrl(board[rowIndex][colIndex])"
                    alt="Country flag"
                    class="country-flag"
                  />
                </div>
                <template v-if="board[rowIndex][colIndex] !== ''">
                  <div class="country-info">{{ board[rowIndex][colIndex] }}</div>
                </template>
              </div>
            </div>
          </div>

          <!-- mobile mrec ad -->
          <mobile-mrec-ad></mobile-mrec-ad>

          <div class="side-panel">
            <div class="panel-info">
              <div class="side-panel-item" @click="handleSidePanelTap">
                <p>Board</p>
                <div class="side-panel-info">
                  <h2>{{ boardId }}</h2>
                </div>
              </div>

              <div class="side-panel-item">
                <p>Score</p>
                <div class="side-panel-info">
                  <h2>{{ score }}</h2>
                </div>
              </div>

              <div class="side-panel-item">
                <p>Guesses left</p>
                <div class="side-panel-info">
                  <h2>{{ infiniteMode ? '∞' : guesses }}</h2>
                </div>
              </div>

              <div class="side-panel-item">
                <p>Infinity Mode</p>
                <div class="side-panel-info">
                  <AppToggle
                    v-if="!extendedPlay"
                    v-model="infiniteMode"
                    @update:modelValue="toggleInfiniteMode"
                    :disabled="isGameOver || (hasMadeGuessInInfiniteMode && infiniteMode)"
                    :customClass="{ selected: infiniteMode, 'infinity-btn btn-primary': true }"
                  >
                    <template v-if="hasMadeGuessInInfiniteMode && infiniteMode">
                      <div class="infinite-tooltip">
                        Once you make a guess in infinite mode, you cannot turn it back off.
                      </div>
                    </template>
                  </AppToggle>
                </div>
              </div>

              <div class="side-panel-item tip">
                <p>Tip</p>
                <div class="side-panel-info-tip">
                  <h3>Click on the category name to learn more</h3>
                </div>
              </div>
            </div>

            <div class="btn-container">
              <button
                v-if="!isGameOver || didRevealHiddenButton"
                class="btn-secondary"
                @click="restartOrGiveUp()"
                :disabled="!anyGuessPlaced() && !isGameOver"
              >
                {{ isGameOver ? 'Restart' : 'Give Up' }}
              </button>
              <GiveUpToast
                v-if="showGiveUpToast"
                title="Are you sure you want to give up?"
                body="If you give up, you will see the answers, but will not be able to finish today’s game."
                :show="showGiveUpToast"
                :primaryButton="{
                  label: 'Give up',
                  onClick: () => giveUp(),
                }"
                :secondary-button="{
                  label: 'Continue playing',
                  onClick: () => (showGiveUpToast = false),
                }"
              />
            </div>
          </div>
        </div>
      </div>
      <p v-else style="color: white">No data available</p>
    </div>
    <button v-if="extendedPlay" class="extended-play-btn btn-primary">Extended Play</button>
    <loading-spinner v-if="isLoadingSummary" style="margin: 0" />

    <!-- Popups -->
    <!-- Do not allow Antarctica to be guessed -->
    <search-popup
      v-if="showSearchPopup && countryData"
      :guessed-countries="board.flat()"
      :countries="
        countryData
          .filter((c) => c.code.toLowerCase() !== 'aq')
          .map((c) => {
            return { code: c.code, name: c.name };
          })
      "
      @close="hideSearch"
      @guess="handleGuess"
    />
    <score-modal
      v-if="showGameOverPopup"
      ref="scoreModal"
      :score="score"
      :boardId="boardId"
      :board="board"
      :archiveMode="archiveMode"
      :liveRarityData="liveRarityData"
      :gameData="gameData"
      :countryData="countryData"
      :answersList="answersList"
      :wrongGuesses="wrongGuesses"
      :hasMadeGuessInInfiniteMode="hasMadeGuessInInfiniteMode"
      :extendedPlay="extendedPlay"
      :rarityPercentages="rarityPercentages"
      @close="hideGameOver"
      @continue-in-extended-play="continueInExtendedPlay"
    />
  </div>
</template>

<script>
  import { computed } from 'vue';
  import axios from 'axios';
  import dayjs from 'dayjs';
  import SearchPopup from './searchPopup.vue';
  import ScoreModal from './gameSummary/scoreModal/scoreModal.vue';
  import config from '../axiospath.js';
  import MobileMrecAd from './ads/mobileMrecAd.vue';
  import LoadingSpinner from './loadingSpinner.vue';
  import AppToggle from './clickables/AppToggle.vue';
  import DateChecker from './dateChecker.vue';
  import getBoardId from '@/utils/getBoardId';
  import getImageUrl from '@/utils/getImageUrl';
  import { getBoard, getCountries, getCountryGeoGridData } from '@/utils/fetchDataFromCDN';
  import NoDataToast from './toasts/noDataToast.vue';
  import { NO_DATA_CATEGORIES, checkGeoGridDataForNoData } from '@/utils/noDataCategoriesUtils';
  import {
    saveBoardToLocalStorage,
    loadBoardFromLocalStorage,
    deleteBoardFromLocalStorage,
  } from '@/utils/storageUtils';
  import GiveUpToast from './toasts/GiveUpToast.vue';
  import { rarityColor, rarityText } from '@/utils/rarityUtils';

  export default {
    inject: ['openCategoryAtlas'],
    components: {
      SearchPopup,
      ScoreModal,
      AppToggle,
      MobileMrecAd,
      LoadingSpinner,
      DateChecker,
      NoDataToast,
      GiveUpToast,
    },
    data() {
      return {
        gameData: null,
        countryData: null,
        liveRarityData: null,
        board: [
          ['', '', ''],
          ['', '', ''],
          ['', '', ''],
        ],
        rarityPercentages: [],
        guesses: 10,
        guessedCountries: [],
        wrongGuesses: [],
        showSearchPopup: false,
        selectedRowIndex: -1,
        selectedColIndex: -1,
        isGameOver: false,
        infiniteMode: false,
        hasMadeGuessInInfiniteMode: false,
        extendedPlay: false,
        searching: false,
        boardId: '',
        archiveMode: false,
        showGameOverPopup: false,
        isLoadingSummary: false,
        isGuessing: false,
        imageUrlCache: {},
        score: 900,
        wrongGuessLocation: [],
        loading: true,
        isNoDataPopup: false,
        noDataPopupCountry: null,
        noDataPopupCategory: null,
        showGiveUpToast: false,
        sidePanelTapCount: 0,
        didRevealHiddenButton: false,
      };
    },
    async mounted() {
      // Check if the user has visited the site before
      const hasVisitedBefore = localStorage.getItem('hasVisitedBefore');
      if (hasVisitedBefore) {
        this.hasVisitedBefore = true;
      } else {
        this.isGameInfoPopup = true;
        this.hasVisitedBefore = true;
        localStorage.setItem('hasVisitedBefore', true);
      }

      // Get the board ID from the URL
      const boardIdFromUrl = window.location.pathname.split('/board/')[1];
      // Get todays board ID from todays date
      const todaysBoardId = getBoardId(dayjs().format('YYYY-MM-DD'));

      if (boardIdFromUrl && +todaysBoardId != +boardIdFromUrl) {
        this.boardId = boardIdFromUrl;
        localStorage.setItem('lastViewedBoardId', this.boardId);
        // don't show future board data
        if (Number(boardIdFromUrl) > todaysBoardId) {
          this.loading = false;
          return;
        }
      } else {
        try {
          localStorage.removeItem('lastViewedBoardId');
          this.boardId = todaysBoardId.toString();
        } catch (error) {
          console.error('Error fetching boards:', error);
        }
      }

      // Tooltips
      document.querySelectorAll('.nav-btn').forEach((button) => {
        const tooltip = button.querySelector('.tooltip');
        const rect = button.getBoundingClientRect();
        tooltip.style.left = `${rect.width / 2}px`;
        tooltip.style.top = `${rect.height + 10}px`;

        // Check if the tooltip goes out of bounds on the right
        const tooltipRect = tooltip.getBoundingClientRect();
        if (tooltipRect.right > window.innerWidth) {
          tooltip.style.left = `${-(tooltipRect.right - window.innerWidth)}px`;
        }
      });

      // Fetch game data, answers, country data, and live rarity data
      await Promise.all([this.fetchGameData(), this.getCountryData(), this.getLiveRarity()]);

      this.loading = false;
    },

    provide() {
      return {
        isGameOverRef: computed(() => this.isGameOver),
      };
    },

    watch: {
      // Load data from local storage when boardId changes
      boardId(newValue, oldValue) {
        if (newValue !== oldValue) {
          // Get todays board ID from todays date
          const todaysBoardId = getBoardId(dayjs().format('YYYY-MM-DD'));
          this.archiveMode = +newValue != +todaysBoardId;
          if (this.archiveMode) {
            localStorage.setItem('lastViewedBoardId', newValue);
          } else {
            localStorage.removeItem('lastViewedBoardId');
          }

          // Hide summary when game changes
          this.showGameOverPopup = false;

          this.loadDataFromLocalStorage();
        }
      },

      // Fetch game data when boardId changes
      async '$route.params.board_id'(newValue) {
        if (newValue !== this.boardId) {
          if (Number(newValue) > getBoardId(dayjs().format('YYYY-MM-DD'))) {
            // don't show future board data
            this.boardId = newValue;
            return;
          }
          this.loading = true;
          this.boardId = newValue;
          this.infiniteMode = false;
          await Promise.all([this.fetchGameData(), this.getLiveRarity()]);
          this.loading = false;
        }
      },

      isGameOver(newVal) {
        if (newVal) {
          // garante que o DOM reflita o popup antes do scroll
          this.$nextTick(() => {
            this.showGameOver(); // já faz o setTimeout + scroll que você criou
          });
        }
      },
    },
    methods: {
      handleSidePanelTap() {
        if (this.didRevealHiddenButton) return;
        this.sidePanelTapCount++;
        if (this.sidePanelTapCount >= 10) {
          this.didRevealHiddenButton = true;
        }
      },
      // Fetch board from the CDN
      async fetchGameData() {
        this.gameData = null;
        this.answersList = null;
        try {
          this.gameData = await getBoard(this.boardId);
          this.answersList = this.gameData.answers;
        } catch (error) {
          console.error('Error fetching game data:', error);
        }
      },

      // Show the search popup for a specific box
      showSearch(rowIndex, colIndex) {
        this.selectedRowIndex = rowIndex;
        this.selectedColIndex = colIndex;
        this.showSearchPopup = true;
        this.searching = true;
      },

      // Hide the search popup
      hideSearch() {
        this.showSearchPopup = false;
        this.searching = false;
      },

      // Restart the game, reset all data
      restartGame() {
        this.deleteUserDataForCurrentBoard();

        this.isGameOver = false;
        this.guesses = 10;
        this.score = 900;
        this.guessedCountries = [];
        this.infiniteMode = false;
        this.board = [
          ['', '', ''],
          ['', '', ''],
          ['', '', ''],
        ];
        this.rarityPercentages = [];
        this.initializeRarityPercentages();
        this.deleteUserDataForCurrentBoard();
      },

      // Extended play
      continueInExtendedPlay() {
        // Check we aren't already in extended play...
        if (this.extendedPlay) return;
        if (this.hasMadeGuessInInfiniteMode) return;

        // Copy data to extended play mode and "reset"
        this.extendedPlay = true;
        this.guesses = 99;
        this.infiniteMode = true;
        this.isGameOver = false;
        this.saveDataToLocalStorage();
      },

      openCategory(categoryId) {
        if (this.openCategoryAtlas) {
          this.openCategoryAtlas(categoryId);
        }
      },

      smoothScrollTo(targetY, duration = 600) {
        const startY = window.scrollY;
        const distance = targetY - startY;
        let startTime = null;

        const step = (timestamp) => {
          if (!startTime) startTime = timestamp;
          const progress = timestamp - startTime;
          const percent = Math.min(progress / duration, 1);

          // easeInOutQuad para suavizar
          const ease = percent < 0.5 ? 2 * percent * percent : -1 + (4 - 2 * percent) * percent;

          window.scrollTo(0, startY + distance * ease);

          if (progress < duration) {
            requestAnimationFrame(step);
          }
        };

        requestAnimationFrame(step);
      },

      // Show the game over popup
      async showGameOver() {
        this.showGameOverPopup = true;
        const isMobile = window.innerWidth <= 768;

        setTimeout(() => {
          this.smoothScrollTo(!isMobile ? 620 : 999, !isMobile ? 600 : 800);
        }, 300);
      },

      showGameOverWithDelay() {
        this.isLoadingSummary = true;
        setTimeout(() => {
          this.isLoadingSummary = false;
          this.showGameOverPopup = true;
        }, 875);
      },

      // Hide the game over popup
      hideGameOver() {
        this.showGameOverPopup = false;
      },

      // Show the no data popup
      showNoDataPopup(categoryName, countryName) {
        this.isNoDataPopup = true;
        this.noDataPopupCountry = countryName;
        this.noDataPopupCategory = categoryName;
      },

      // Hide the no data popup
      hideNoDataPopup() {
        this.isNoDataPopup = false;
        this.noDataPopupCountry = null;
        this.noDataPopupCategory = null;
      },

      // Fetch country list from CDN
      async getCountryData() {
        try {
          this.countryData = await getCountries();
        } catch (error) {
          console.error('Error fetching country data:', error);
        }
      },

      // Fetch country geogrid data from CDN
      async getGeogridData(countryCode) {
        try {
          return await getCountryGeoGridData(countryCode);
        } catch (error) {
          console.error('Error fetching geogrid data:', error);
          return null;
        }
      },

      // Fetch live rarity data from the API
      async getLiveRarity() {
        try {
          const liveRarityDataResponse = await axios.get(
            `${config.baseURL}/game/rarity/` + this.boardId
          );
          this.liveRarityData = liveRarityDataResponse.data;
        } catch (error) {
          console.error('Error fetching country data:', error);
        }
      },

      // Toggle infinite mode
      toggleInfiniteMode(newValue) {
        console.log('entrei aqui', newValue);
        if (newValue) {
          this.previousGuesses = this.guesses;
          console.log('this.guesses', JSON.stringify(this.guesses, null, 2));
          console.log('this.previousGuesses', JSON.stringify(this.previousGuesses, null, 2));
        } else {
          this.guesses = this.previousGuesses;
          console.log('this.guesses', JSON.stringify(this.guesses, null, 2));
        }
        this.infiniteMode = newValue;
        this.saveDataToLocalStorage();
      },

      /**
       * Check if we have no data for a country for a particular category
       * @param categoryId the category to check
       * @param countryCode the country to check
       *
       * @returns boolean - true if the country does not have any data for the category, false otherwise
       */
      async checkForNullCategoryData(categoryId, countryCode) {
        if (!NO_DATA_CATEGORIES.includes(categoryId)) {
          // this is not a no data category, no need to check the country data
          return false;
        }

        try {
          const data = await this.getGeogridData(countryCode);
          if (!data) return false;
          return checkGeoGridDataForNoData(categoryId, data);
        } catch (e) {
          console.error('checkForNullCategoryData: ', e);
          return false;
        }
      },

      // Handle the guess made by the user
      async handleGuess({ code, name: guess }) {
        if (this.isGuessing) {
          return;
        }

        this.isGuessing = true;
        //increment play count when first guess placed
        if (this.guessedCountries.length === 0) {
          const url = `${config.baseURL}/game/stats/${this.boardId}?score=${this.score}&flag=1`;
          try {
            axios.post(url);
          } catch (error) {
            console.error('Error making request:', error);
          }
        }
        // Check if the guess has already been used
        if (this.checkIfCountryHasBeenUsed(guess)) {
          return;
        }

        // Make the guess
        try {
          const index =
            this.selectedRowIndex * this.gameData.columns.length + this.selectedColIndex + 1;

          const correct = this.answersList['match_box_' + index].includes(guess);

          // Before counting the guess, check if this is a no data category
          // Don't count the guess if we have no data for the category
          // Also save wrong guess data
          if (!correct) {
            const colCategory = this.gameData.columns[this.selectedColIndex];
            const rowCategory = this.gameData.rows[this.selectedRowIndex];

            const isColNull = await this.checkForNullCategoryData(colCategory.id, code);
            if (isColNull) {
              this.showNoDataPopup(colCategory.name, guess);
              return;
            }

            const isRowNull = await this.checkForNullCategoryData(rowCategory.id, code);
            if (isRowNull) {
              this.showNoDataPopup(rowCategory.name, guess);
              return;
            }

            // if answer is wrong but accepted, save to wrongGuesses
            this.wrongGuesses.push({ index, code, name: guess });
          }

          this.guessedCountries.push(guess);

          // Update rarity
          if (correct) {
            const url = `${config.baseURL}/game/rarity/${this.boardId}?guess=${guess}&index=${index}`;
            try {
              axios.post(url);
            } catch (error) {
              console.error('Error making request:', error);
            }

            //calculate rarity of guess
            const total = this.liveRarityData['match_box_' + index]['total'];
            const count = this.liveRarityData['match_box_' + index][guess];
            let rarityPercentage;

            rarityPercentage = ((count / total) * 100).toFixed(1);
            if (isNaN(rarityPercentage)) {
              rarityPercentage = 0;
            }
            this.score = (this.score - (100 - rarityPercentage)).toFixed(1);

            // Ensure rarityPercentages[this.selectedRowIndex] is initialized as an array
            if (!this.rarityPercentages[this.selectedRowIndex]) {
              this.rarityPercentages[this.selectedRowIndex] = [];
            }

            // Set rarityPercentage value
            this.rarityPercentages[this.selectedRowIndex][this.selectedColIndex] = rarityPercentage;
            this.board[this.selectedRowIndex][this.selectedColIndex] = guess;
            this.getImageUrl(guess);
          }

          // Hide the search popup
          this.hideSearch();
          if (!this.infiniteMode) {
            this.guesses--;
          } else {
            this.hasMadeGuessInInfiniteMode = true;
          }

          // Show wrong animation on box
          if (!correct) {
            this.setWrongGuessLocation();
          }

          // Check if the game is won or lost
          if (this.checkIfWon()) {
            this.showGameOverWithDelay();
            this.isGameOver = true;
          } else if (this.guesses === 0) {
            this.showGameOverWithDelay();
            this.isGameOver = true;
          }
        } catch (error) {
          console.error('Error making guess:', error);
        } finally {
          // Reset the flag when the guess request is complete
          this.isGuessing = false;
        }
        this.saveDataToLocalStorage();
      },

      // Check if country has been used
      checkIfCountryHasBeenUsed(guess) {
        if (this.board.flat().some((box) => box === guess)) {
          return true;
        } else {
          return false;
        }
      },

      setWrongGuessLocation() {
        this.wrongGuessLocation = [this.selectedRowIndex, this.selectedColIndex];
        setTimeout(() => {
          this.wrongGuessLocation = [];
        }, 500);
      },

      // Check if the game is won
      checkIfWon() {
        if (this.board.flat().every((box) => box !== '')) {
          // this.getLiveRarity();
          return true;
        } else {
          return false;
        }
      },

      // Get the image URL of the guessed country
      getImageUrl,

      // Give up the game
      giveUp() {
        // this.getLiveRarity();
        this.isGameOver = true;
        this.saveDataToLocalStorage();
        this.showGameOver();
        this.showGiveUpToast = false;
      },

      // Restart the game or give up based on the game state
      restartOrGiveUp() {
        if (this.isGameOver) {
          this.restartGame();
          // this.getLiveRarity();
        } else {
          this.showGiveUpToast = true;
        }
      },

      // Initialize rarity percentages array
      initializeRarityPercentages() {
        this.rarityPercentages = this.board.map((row) => Array(row.length).fill(''));
      },

      // Function to get the rarity text based on the percentage
      getRarityText(rowIndex, colIndex) {
        const rarityPercentage = this.rarityPercentages[rowIndex][colIndex];
        if (rarityPercentage === 0) {
          return 'Legendary - First';
        } else {
          const parsedPercentage = parseFloat(rarityPercentage);
          if (!isNaN(parsedPercentage)) {
            return `${rarityText(parsedPercentage)} - ${parsedPercentage}%`;
          } else {
            return `Legendary - ${rarityPercentage}`;
          }
        }
      },

      // Function to get the rarity styling based on the percentage
      getRarityStyle(rowIndex, colIndex) {
        const rarityPercentage = parseFloat(this.rarityPercentages[rowIndex][colIndex]);
        return { backgroundColor: rarityColor(rarityPercentage) };
      },

      // Check if any guess has been placed
      anyGuessPlaced() {
        return this.guessedCountries.length > 0;
      },

      // Save the game data to local storage
      saveDataToLocalStorage() {
        let boardKey = this.boardId;
        if (this.extendedPlay) {
          boardKey += 'e';
        }
        saveBoardToLocalStorage(boardKey, {
          guessedCountries: this.guessedCountries,
          guesses: this.guesses,
          previousGuesses: this.previousGuesses,
          isGameOver: this.isGameOver,
          board: this.board,
          rarityPercentages: this.rarityPercentages,
          score: this.score,
          wrongGuesses: this.wrongGuesses,
          infiniteMode: this.infiniteMode,
          hasMadeGuessInInfiniteMode: this.hasMadeGuessInInfiniteMode,
          archiveMode: this.archiveMode,
        });
      },

      // Load the game data from local storage
      loadDataFromLocalStorage() {
        let foundSaveData = false;
        for (const extended of [true, false]) {
          let boardKey = this.boardId;
          if (extended) {
            boardKey += 'e';
          }
          const savedData = loadBoardFromLocalStorage(boardKey);
          if (savedData) {
            foundSaveData = true;
            this.extendedPlay = extended;

            // Update the component's state with the loaded data
            this.guessedCountries = savedData.guessedCountries;
            this.guesses = savedData.guesses;
            this.isGameOver = !!savedData.isGameOver;
            this.previousGuesses = savedData.previousGuesses;
            this.infiniteMode = !!savedData.infiniteMode;
            this.hasMadeGuessInInfiniteMode = !!savedData.hasMadeGuessInInfiniteMode;
            this.board = savedData.board;
            this.rarityPercentages = savedData.rarityPercentages;
            this.score = savedData.score;
            this.wrongGuesses = savedData.wrongGuesses ?? [];
            this.archiveMode = savedData.archiveMode ?? false;
            break;
          }
        }
        if (!foundSaveData) {
          // Reset component's state if no data is found
          this.resetState();
        }
      },

      // Delete user data for the current board from local storage
      deleteUserDataForCurrentBoard() {
        let boardKey = this.boardId;
        if (this.extendedPlay) {
          boardKey += 'e';
        }
        deleteBoardFromLocalStorage(boardKey);
      },

      // Reset the component's state
      resetState() {
        this.extendedPlay = false;
        this.guessedCountries = [];
        this.guesses = 10;
        this.wrongGuesses = [];
        this.isGameOver = false;
        this.infiniteMode = false;
        this.hasMadeGuessInInfiniteMode = false;
        this.board = [
          ['', '', ''],
          ['', '', ''],
          ['', '', ''],
        ];
        this.rarityPercentages = [];
        this.score = 900;
      },

      sendEmail() {
        // Prompt user to send email
        const email = 'geogridgame@gmail.com';
        const mailtoLink = `mailto:${email}`;

        window.location.href = mailtoLink;
      },
      isFirefox() {
        return navigator.userAgent.includes('Firefox');
      },
    },
  };
</script>

<style>
  body {
    overflow-x: hidden;
  }

  :root {
    background-color: rgb(53, 53, 72);
    --cell-width: 150px;
    --cell-width-tablet: 120px;
    --cell-width-mobile: 90px;
    --cell-width-small-mobile: 80px;
    --cell-width-minuscule-mobile: 70px;
  }

  .logo {
    width: 200px;
    max-width: 100%;
  }

  .logo img {
    width: 100%;
    height: auto;
  }

  .filler {
    width: var(--cell-width);
  }

  .prompt {
    display: flex;
    justify-content: center;
    align-items: center;
    color: rgb(223, 223, 223);
    font-size: 14px;
    background-color: rgba(76, 76, 103, 1);
    width: var(--cell-width);
    min-height: 60px;
    padding: 10px;
    box-sizing: border-box;
    border-radius: 16px;
    cursor: pointer;
  }

  .prompt:hover {
    background-color: rgba(42, 42, 58, 1);
  }

  .row-prompt {
    height: var(--cell-width);
  }

  .row-prompt,
  .filler {
    margin-right: 8px;
  }

  .board {
    display: flex;
    flex-direction: row;
    align-items: stretch;
    justify-content: center;
    margin-right: auto;
    /* margin-top: 90px; */
    gap: 12px;
  }

  .panel {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .row {
    display: flex;
    align-items: stretch;
    height: var(--cell-width);
    gap: 8px;
  }

  .row:first-child {
    height: max-content;
    margin-bottom: 8px;
    margin-right: 0;
  }

  .box {
    width: var(--cell-width);
    height: 100%;
    background-color: rgba(0, 0, 0, 0.2);
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    transition: background-color 0.25s;
    border-radius: 12px;
    box-sizing: border-box;
    padding: 6px;
  }

  .btn-primary {
    height: 44px;
    width: 100%;
    cursor: pointer;
    background-color: rgba(147, 147, 236, 1);
    color: rgba(26, 26, 26, 1);
    border: 1px rgba(147, 147, 236, 1);
    padding: 6px 12px;
    border-radius: 12px;
    transition: background-color 0.3s;
    font-size: 14px;
    font-family: 'Oxygen', Helvetica, Arial, sans-serif;
    font-weight: 700;
    box-sizing: border-box;
  }

  .btn-secondary {
    height: 44px;
    width: 100%;
    cursor: pointer;
    background-color: transparent;
    color: rgba(147, 147, 236, 1);
    border: 2px rgba(147, 147, 236, 1) solid;
    padding: 6px 12px;
    border-radius: 12px;
    transition: background-color 0.3s;
    font-size: 14px;
    font-family: 'Oxygen', Helvetica, Arial, sans-serif;
    font-weight: 700;
    box-sizing: border-box;
  }

  .archive-badge {
    display: inline;
    background-color: rgba(255, 255, 255, 0) !important;
    border: 1px solid #fff;
    box-sizing: border-box;
    cursor: default;
  }

  .guessed {
    cursor: initial;
  }
  .box:not(.guessed):hover {
    background-color: rgba(76, 76, 103, 1);
  }

  .highlighted {
    background-color: rgb(147 147 236);
  }

  .wrong {
    background-color: #aa0817;
    transition: background-color 0.25s;
  }
  .prev-grids-btn {
    margin-top: 10px;
    margin-bottom: 20px;
  }

  .restart-btn,
  .infinity-btn,
  .show-summary-btn,
  .extended-play-btn,
  .archive-badge {
    margin-bottom: 15px;
  }

  .infinity-btn {
    position: relative;
  }

  .infinite-tooltip {
    position: absolute;
    bottom: -36px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(32, 32, 44, 1);
    color: #fff;
    padding: 5px 10px;
    border-radius: 12px;
    white-space: nowrap;
    z-index: 1000;
    opacity: 0;
    transition: opacity 0.4s;
    pointer-events: none;
  }

  .infinity-btn:hover .infinite-tooltip {
    opacity: 1;
  }

  .extended-play-btn {
    background-color: #aa0817;
  }

  .restart-btn,
  .show-summary-btn,
  .archive-badge {
    margin-left: 10px;
  }

  .btn-primary:hover {
    background-color: rgba(184, 184, 249, 1);
  }

  .btn-secondary:hover {
    background-color: rgba(0, 0, 0, 0.2);
  }

  .btn-secondary:disabled {
    background-color: rgba(73, 73, 90, 255);
    color: rgba(189, 189, 219, 255);
    border-color: rgba(184, 184, 224, 1);
    opacity: 0.5;
    cursor: not-allowed;
  }

  .flag-container {
    width: 100%;
    flex-grow: 1;
    overflow: hidden;
    margin: 6px 0;
  }
  .country-flag {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .country-info {
    width: 100%;
    min-height: 14%;
    display: flex;
    justify-content: center;
    align-items: center;
    font-weight: bold;
    box-sizing: border-box;
    overflow: hidden;
    font-size: 12px;
    color: white;
    border-radius: 8px;
    flex-shrink: 0;
    background-color: #373769;
  }
  .rarity-element {
    width: 100%;
    height: 11%;
    display: flex;
    justify-content: center;
    align-items: center;
    color: white;
    font-size: 10px;
    font-weight: bold;
    border-radius: 8px;
  }

  .side-panel {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  .panel-info {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .side-panel-item {
    width: 120px;
    background-color: rgba(76, 76, 103, 1);
    border-radius: 16px;
    padding: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
  }

  .side-panel-item p {
    color: rgb(223, 223, 223);
    font-weight: 400;
    font-size: 14px;
    margin: 0;
    text-align: center;
  }

  .side-panel-info {
    width: 100%;
    background-color: rgba(53, 53, 72, 1);
    border-radius: 12px;
    padding: 6px;
    display: flex;
    justify-content: center;
    align-items: center;
    box-sizing: border-box;
    max-height: 40px;
    text-align: center;
  }

  .side-panel-info-tip {
    width: 100%;
    background-color: rgba(53, 53, 72, 1);
    border-radius: 12px;
    padding: 6px;
    display: flex;
    justify-content: center;
    align-items: center;
    box-sizing: border-box;
    min-height: 40px;
    text-align: center;
  }

  .side-panel-info h2 {
    color: rgb(223, 223, 223);
    font-weight: 700;
    font-size: 28px;
    margin: 0;
  }

  .side-panel-info-tip h3 {
    color: rgb(223, 223, 223);
    font-weight: 400;
    font-size: 14px;
    margin: 0;
    padding: 2px;
    text-align: left;
    line-height: 1.2;
    letter-spacing: 0px;
  }

  .btn-container {
    display: flex;
    justify-content: center;
    width: 100%;
  }

  .category-help-msg {
    color: rgb(134, 136, 164);
    font-size: 12px;
    margin-top: 1px;
    font-weight: bold;
  }

  .llc-msg {
    color: rgb(134, 136, 164);
    font-size: 12px;
    font-weight: bold;
  }

  .contact-msg {
    color: rgb(134, 136, 164);
    font-size: 12px;
    font-weight: bold;
    margin-left: 20px;
  }

  .plays-msg {
    color: rgb(134, 136, 164);
    font-size: 14px;
    margin-top: 2px;
    margin-bottom: 2px;
    font-weight: bold;
  }

  @media screen and (max-width: 992px) {
    .board {
      margin-right: 0;
      margin-left: -25px;
      margin-top: 60px;
    }
    .guesses-and-score {
      margin-right: 0;
    }
  }

  @media screen and (max-width: 768px) {
    .plays-msg {
      font-size: 12px;
    }
    .llc-msg,
    .contact-msg {
      font-size: 10px;
    }
    .board {
      gap: 3px;
      flex-direction: column;
      align-items: center;
    }
    .panel {
      gap: 3px;
    }
    .side-panel {
      /*width is 4 boxes width and the gap of 3px for each*/
      width: calc(4 * var(--cell-width-tablet) + 9px);
      gap: 25px;
      margin-top: 12px;
    }
    .panel-info {
      flex-direction: row;
      justify-content: space-between;
      width: 100%;
      flex-wrap: wrap;
    }
    .side-panel-item {
      min-width: var(--cell-width-tablet);
      width: auto;
      border-radius: 12px;
    }
    .side-panel-item p {
      font-size: 12px;
    }
    .side-panel-info {
      border-radius: 10px;
    }
    .side-panel-info h2 {
      font-size: 22px;
    }
    .tip {
      width: 60%;
    }
    .side-panel-info-tip h3 {
      font-size: 12px;
    }
    .filler {
      width: var(--cell-width-tablet);
    }
    .prompt {
      overflow: hidden;
      width: var(--cell-width-tablet);
      min-height: 50px;
      font-size: 12px;
    }
    .row-prompt {
      height: var(--cell-width-tablet);
    }
    .row {
      height: var(--cell-width-tablet);
      gap: 3px;
    }

    .box {
      width: var(--cell-width-tablet);
      font-size: 14px;
      padding: 4px;
    }

    .country-info {
      font-size: 10px;
    }

    .rarity-element {
      font-size: 8px;
    }

    .flag-container {
      margin: 4px 0;
    }
  }

  @media screen and (max-width: 480px) {
    .board {
      margin: 12px 0 0;
      gap: 2px;
    }
    .filler {
      width: var(--cell-width-mobile);
    }
    .prompt {
      width: var(--cell-width-mobile);
      font-size: 10px;
      padding: 8px;
    }
    .row-prompt {
      height: var(--cell-width-mobile);
    }
    .row {
      height: var(--cell-width-mobile);
      gap: 2px;
    }
    .box {
      width: var(--cell-width-mobile);
    }
    .side-panel {
      /*width is 4 boxes width and the gap of 2px for each*/
      width: calc(4 * var(--cell-width-mobile) + 8px);
    }
    .side-panel-item {
      min-width: var(--cell-width-mobile);
    }
    .side-panel-item p {
      font-size: 10px;
    }
    .side-panel-info h2 {
      font-size: 18px;
    }
    .side-panel-info-tip h3 {
      font-size: 10px;
    }
    .guesses-and-score {
      font-size: 14px;
    }
    .country-info {
      font-size: 8px;
    }
    .tip {
      width: 60%;
    }

    .rarity-element {
      font-size: 7px;
    }
  }

  @media screen and (max-width: 375px) {
    .filler,
    .prompt,
    .box {
      width: var(--cell-width-small-mobile);
    }
    .row-prompt,
    .row {
      height: var(--cell-width-small-mobile);
    }
    .side-panel {
      /*width is 4 boxes width and the gap of 1px for each*/
      width: calc(4 * var(--cell-width-small-mobile) + 8px);
    }
    .side-panel-item {
      min-width: var(--cell-width-small-mobile);
    }
  }
  @media screen and (max-width: 335px) {
    .filler,
    .prompt,
    .box {
      width: var(--cell-width-minuscule-mobile);
      border-radius: 10px;
    }
    .row-prompt,
    .row {
      height: var(--cell-width-minuscule-mobile);
    }
    .side-panel {
      /*width is 4 boxes width and the gap of 1px for each*/
      width: calc(4 * var(--cell-width-minuscule-mobile) + 4px);
    }
    .side-panel-item {
      min-width: var(--cell-width-minuscule-mobile);
      border-radius: 10px;
    }
    .side-panel-item p {
      font-size: 10px;
    }
    .side-panel-info h2 {
      font-size: 18px;
    }
    .tip {
      width: 57%;
    }
    .side-panel-info-tip h3 {
      font-size: 10px;
    }
  }
  @keyframes shake {
    0% {
      transform: translateX(0);
    }

    20% {
      transform: translateX(-8px);
    }

    40% {
      transform: translateX(8px);
    }

    60% {
      transform: translateX(-8px);
    }

    80% {
      transform: translateX(8px);
    }

    100% {
      transform: translateX(0);
    }
  }

  .animate-shake {
    animation: shake 0.5s;
  }

  @keyframes gradientMove {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }
</style>
